// Web Push subscription helper. Works with native Service Worker + VAPID.
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

export const isPushSupported = () =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

const getRegistration = async () => {
  // Wait for the SW we registered in index.html to be ready
  const reg = await navigator.serviceWorker.ready;
  return reg;
};

const fetchVapidKey = async () => {
  const { data } = await axios.get(`${API}/push/vapid-public-key`);
  if (!data?.publicKey) throw new Error('No VAPID key configured on the server');
  return data.publicKey;
};

/**
 * Subscribe the current user to web push and register on the backend.
 * @param {'customer'|'driver'} role
 * @param {object} authHeaders  authorization headers for the API
 */
export const subscribeToPush = async (role, authHeaders) => {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };

  let permission = Notification.permission;
  if (permission === 'default') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') return { ok: false, reason: 'denied' };

  const reg = await getRegistration();
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const vapidKey = await fetchVapidKey();
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });
  }
  const url = role === 'driver' ? `${API}/driver/push/subscribe` : `${API}/customer/push/subscribe`;
  await axios.post(url, sub.toJSON(), { headers: authHeaders });
  return { ok: true };
};

export const unsubscribeFromPush = async (role, authHeaders) => {
  if (!isPushSupported()) return { ok: false };
  const reg = await getRegistration();
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { ok: true };
  const json = sub.toJSON();
  const url = role === 'driver' ? `${API}/driver/push/unsubscribe` : `${API}/customer/push/unsubscribe`;
  try {
    await axios.post(url, json, { headers: authHeaders });
  } catch {}
  await sub.unsubscribe();
  return { ok: true };
};

export const getCurrentPushStatus = async () => {
  if (!isPushSupported()) return 'unsupported';
  if (Notification.permission === 'denied') return 'denied';
  if (Notification.permission === 'default') return 'default';
  // permission granted — check if we actually have a subscription
  try {
    const reg = await getRegistration();
    const sub = await reg.pushManager.getSubscription();
    return sub ? 'subscribed' : 'granted-no-sub';
  } catch {
    return 'default';
  }
};
